/**
 * Hooks for Velociraptor Settings (Admin)
 *
 * Hooks for managing Velociraptor service configuration.
 * All hooks require EvidenX Admin role.
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  VelociraptorConfig,
  ServiceStatusResponse,
  InstallResult,
  ActionResponse,
  ConnectivityTestResult,
  TestCollectorResult,
  PortStatus,
  UpgradeResult,
  TestEndpoint,
  UpdateConfigInput,
  InstallInput,
  TestCollectorInput,
} from '../types/velociraptor-settings'
import {
  getConfig,
  updateConfig,
  getStatus,
  installService,
  startService,
  stopService,
  upgradeService,
  testConnection,
  generateTestCollector,
  getTestEndpoints,
  checkDefaultPorts,
  checkPorts,
  isConfigured,
  isRunning,
  downloadCollector,
} from '../services/velociraptor-settings'

interface ServiceActionOptions {
  setLoading: (busy: boolean) => void
  setError: (message: string | null) => void
  fallbackMessage: string
}

interface ServiceActionResult {
  success: boolean
  errorMessage?: string | null
}

async function runServiceAction<T extends ServiceActionResult>(
  options: ServiceActionOptions,
  fn: () => Promise<T>
): Promise<T | null> {
  options.setLoading(true)
  options.setError(null)
  try {
    const result = await fn()
    if (!result.success && result.errorMessage) {
      options.setError(result.errorMessage)
    }
    return result
  } catch (err) {
    options.setError(err instanceof Error ? err.message : options.fallbackMessage)
    return null
  } finally {
    options.setLoading(false)
  }
}

// =============================================================================
// useVelociraptorConfig - Configuration management
// =============================================================================

interface UseVelociraptorConfigResult {
  config: VelociraptorConfig | null
  loading: boolean
  error: string | null
  isConfigured: boolean
  refetch: () => Promise<void>
  update: (input: UpdateConfigInput) => Promise<VelociraptorConfig | null>
  updating: boolean
}

/**
 * Hook for managing Velociraptor configuration
 */
export function useVelociraptorConfig(): UseVelociraptorConfigResult {
  const [config, setConfig] = useState<VelociraptorConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await getConfig()
      setConfig(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch Velociraptor config')
      setConfig(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const update = useCallback(
    async (input: UpdateConfigInput): Promise<VelociraptorConfig | null> => {
      setUpdating(true)
      setError(null)

      try {
        const data = await updateConfig(input)
        setConfig(data)
        return data
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update Velociraptor config')
        return null
      } finally {
        setUpdating(false)
      }
    },
    []
  )

  return {
    config,
    loading,
    error,
    isConfigured: config ? isConfigured(config) : false,
    refetch: fetch,
    update,
    updating,
  }
}

// =============================================================================
// useVelociraptorStatus - Service status
// =============================================================================

interface UseVelociraptorStatusResult {
  status: ServiceStatusResponse | null
  loading: boolean
  error: string | null
  isRunning: boolean
  refetch: () => Promise<void>
}

/**
 * Hook for checking Velociraptor service status
 */
export function useVelociraptorStatus(): UseVelociraptorStatusResult {
  const [status, setStatus] = useState<ServiceStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await getStatus()
      setStatus(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch service status')
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return {
    status,
    loading,
    error,
    isRunning: status ? isRunning(status) : false,
    refetch: fetch,
  }
}

// =============================================================================
// useVelociraptorServiceControl - Service lifecycle management
// =============================================================================

interface UseVelociraptorServiceControlResult {
  installing: boolean
  starting: boolean
  stopping: boolean
  upgrading: boolean
  error: string | null
  install: (input: InstallInput) => Promise<InstallResult | null>
  start: () => Promise<ActionResponse | null>
  stop: () => Promise<ActionResponse | null>
  upgrade: () => Promise<UpgradeResult | null>
  clearError: () => void
}

/**
 * Hook for controlling Velociraptor service lifecycle
 */
export function useVelociraptorServiceControl(): UseVelociraptorServiceControlResult {
  const [installing, setInstalling] = useState(false)
  const [starting, setStarting] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [upgrading, setUpgrading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const install = useCallback(
    (input: InstallInput) =>
      runServiceAction<InstallResult>(
        { setLoading: setInstalling, setError, fallbackMessage: 'Failed to install Velociraptor' },
        () => installService(input)
      ),
    []
  )

  const start = useCallback(
    () =>
      runServiceAction<ActionResponse>(
        { setLoading: setStarting, setError, fallbackMessage: 'Failed to start Velociraptor' },
        () => startService()
      ),
    []
  )

  const stop = useCallback(
    () =>
      runServiceAction<ActionResponse>(
        { setLoading: setStopping, setError, fallbackMessage: 'Failed to stop Velociraptor' },
        () => stopService()
      ),
    []
  )

  const upgradeFunc = useCallback(
    () =>
      runServiceAction<UpgradeResult>(
        { setLoading: setUpgrading, setError, fallbackMessage: 'Failed to upgrade Velociraptor' },
        () => upgradeService()
      ),
    []
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    installing,
    starting,
    stopping,
    upgrading,
    error,
    install,
    start,
    stop,
    upgrade: upgradeFunc,
    clearError,
  }
}

// =============================================================================
// useVelociraptorConnectivity - Connectivity testing
// =============================================================================

interface UseVelociraptorConnectivityResult {
  testResult: ConnectivityTestResult | null
  testing: boolean
  error: string | null
  test: () => Promise<ConnectivityTestResult | null>
}

/**
 * Hook for testing Velociraptor connectivity
 */
export function useVelociraptorConnectivity(): UseVelociraptorConnectivityResult {
  const [testResult, setTestResult] = useState<ConnectivityTestResult | null>(null)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const test = useCallback(async (): Promise<ConnectivityTestResult | null> => {
    setTesting(true)
    setError(null)

    try {
      const result = await testConnection()
      setTestResult(result)
      return result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test connectivity')
      return null
    } finally {
      setTesting(false)
    }
  }, [])

  return {
    testResult,
    testing,
    error,
    test,
  }
}

// =============================================================================
// useTestCollector - Test collector generation
// =============================================================================

interface UseTestCollectorResult {
  result: TestCollectorResult | null
  generating: boolean
  downloading: boolean
  error: string | null
  generate: (input: TestCollectorInput) => Promise<TestCollectorResult | null>
  download: (flowId: string) => Promise<boolean>
}

/**
 * Hook for generating test collectors
 */
export function useTestCollector(): UseTestCollectorResult {
  const [result, setResult] = useState<TestCollectorResult | null>(null)
  const [generating, setGenerating] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = useCallback(
    async (input: TestCollectorInput): Promise<TestCollectorResult | null> => {
      setGenerating(true)
      setError(null)

      try {
        const data = await generateTestCollector(input)
        setResult(data)
        return data
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate test collector')
        return null
      } finally {
        setGenerating(false)
      }
    },
    []
  )

  const download = useCallback(async (flowId: string): Promise<boolean> => {
    setDownloading(true)
    setError(null)

    try {
      await downloadCollector(flowId)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download collector')
      return false
    } finally {
      setDownloading(false)
    }
  }, [])

  return {
    result,
    generating,
    downloading,
    error,
    generate,
    download,
  }
}

// =============================================================================
// useTestEndpoints - Test endpoints list
// =============================================================================

interface UseTestEndpointsResult {
  endpoints: TestEndpoint[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook for fetching test endpoints
 */
export function useTestEndpoints(): UseTestEndpointsResult {
  const [endpoints, setEndpoints] = useState<TestEndpoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await getTestEndpoints()
      setEndpoints(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch test endpoints')
      setEndpoints([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return {
    endpoints,
    loading,
    error,
    refetch: fetch,
  }
}

// =============================================================================
// usePortAvailability - Port availability checking
// =============================================================================

interface UsePortAvailabilityResult {
  ports: PortStatus[]
  checking: boolean
  error: string | null
  checkDefault: () => Promise<PortStatus[] | null>
  checkCustom: (ports: number[]) => Promise<PortStatus[] | null>
}

/**
 * Hook for checking port availability
 */
export function usePortAvailability(): UsePortAvailabilityResult {
  const [ports, setPorts] = useState<PortStatus[]>([])
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const checkDefault = useCallback(async (): Promise<PortStatus[] | null> => {
    setChecking(true)
    setError(null)

    try {
      const data = await checkDefaultPorts()
      setPorts(data)
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check port availability')
      return null
    } finally {
      setChecking(false)
    }
  }, [])

  const checkCustom = useCallback(async (portList: number[]): Promise<PortStatus[] | null> => {
    setChecking(true)
    setError(null)

    try {
      const data = await checkPorts({ ports: portList })
      setPorts(data)
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check port availability')
      return null
    } finally {
      setChecking(false)
    }
  }, [])

  return {
    ports,
    checking,
    error,
    checkDefault,
    checkCustom,
  }
}

// =============================================================================
// useVelociraptorSettings - Combined hook for settings page
// =============================================================================

interface UseVelociraptorSettingsResult {
  // Config
  config: VelociraptorConfig | null
  configLoading: boolean
  configError: string | null
  isConfigured: boolean
  refetchConfig: () => Promise<void>
  updateConfig: (input: UpdateConfigInput) => Promise<VelociraptorConfig | null>
  updatingConfig: boolean

  // Status
  status: ServiceStatusResponse | null
  statusLoading: boolean
  statusError: string | null
  isRunning: boolean
  refetchStatus: () => Promise<void>

  // Service control
  serviceControl: UseVelociraptorServiceControlResult

  // Connectivity
  connectivity: UseVelociraptorConnectivityResult

  // Test collector
  testCollector: UseTestCollectorResult

  // Ports
  portAvailability: UsePortAvailabilityResult
}

/**
 * Combined hook for the Velociraptor settings page
 */
export function useVelociraptorSettings(): UseVelociraptorSettingsResult {
  const configHook = useVelociraptorConfig()
  const statusHook = useVelociraptorStatus()
  const serviceControl = useVelociraptorServiceControl()
  const connectivity = useVelociraptorConnectivity()
  const testCollector = useTestCollector()
  const portAvailability = usePortAvailability()

  return {
    // Config
    config: configHook.config,
    configLoading: configHook.loading,
    configError: configHook.error,
    isConfigured: configHook.isConfigured,
    refetchConfig: configHook.refetch,
    updateConfig: configHook.update,
    updatingConfig: configHook.updating,

    // Status
    status: statusHook.status,
    statusLoading: statusHook.loading,
    statusError: statusHook.error,
    isRunning: statusHook.isRunning,
    refetchStatus: statusHook.refetch,

    // Service control
    serviceControl,

    // Connectivity
    connectivity,

    // Test collector
    testCollector,

    // Ports
    portAvailability,
  }
}
